/**
 * GreenRoom Component - Pre-join setup and device configuration
 *
 * Features:
 * - Camera/microphone preview
 * - Device selection
 * - Quality settings
 * - Session configuration
 * - Guest invite management
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStudioStore } from '../App';
import { getMediaConstraints, QUALITY_PRESETS } from '../lib/recorder';
import { createStudioSession, SessionConfig } from '../lib/session';

interface GreenRoomProps {
  isGuest?: boolean;
}

// Truncate device labels to fit within dropdown
function truncateDeviceLabel(label: string, maxLength: number = 28): string {
  if (!label || label.length <= maxLength) return label;
  return label.substring(0, maxLength - 1) + '…';
}

export function GreenRoom({ isGuest = false }: GreenRoomProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Store
  const { token, userId, userName, setSessionConfig } = useStudioStore();

  // Local state
  const [sessionTitle, setSessionTitle] = useState('');
  const [selectedQuality, setSelectedQuality] = useState<keyof typeof QUALITY_PRESETS>('1080p');
  const [enableChat, setEnableChat] = useState(true);
  const [enableGreenRoom, setEnableGreenRoom] = useState(false);
  const [maxParticipants, setMaxParticipants] = useState(10);

  // Device state
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

  // UI state
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studioMode, setStudioMode] = useState<'record' | 'live'>('record');

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);

  // Guest mode - get session info from URL
  const guestSessionId = searchParams.get('session');
  const guestToken = searchParams.get('token');
  const guestMeetingId = searchParams.get('meeting');

  // Quick start mode - bypass green room for hosts
  const quickStart = searchParams.get('quick') === 'true';

  // Auto-start session for hosts in quick mode
  useEffect(() => {
    if (quickStart && !isGuest && token && !isLoading) {
      console.log('[GreenRoom] Quick start mode - auto-starting session');
      // Small delay to ensure devices are ready
      const timer = setTimeout(() => {
        startSessionQuick();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [quickStart, isGuest, token]);

  // Quick start session with default settings (bypasses green room)
  const startSessionQuick = async () => {
    console.log('[GreenRoom] startSessionQuick called - bypassing green room');

    if (!token) {
      console.error('[GreenRoom] No auth token available');
      setError('Please log in to start a session.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const config: SessionConfig = {
        title: `Recording Session ${new Date().toLocaleString()}`,
        hostId: userId || 'host',
        hostName: userName || 'Host',
        quality: '1080p',
        enableChat: true,
        enableGreenRoom: false,
        enableRecording: true,
        maxParticipants: 10
      };

      console.log('[GreenRoom] Quick start - creating session with defaults:', config);
      setSessionConfig(config);

      const session = createStudioSession(config, token, {
        onStateChange: (state) => {
          console.log('[GreenRoom] Session state changed:', state);
          useStudioStore.getState().setSessionState(state);
        },
        onError: (err) => {
          console.error('[Session] Error:', err);
          setError(err.message);
        }
      });

      console.log('[GreenRoom] Initializing session...');
      await session.initialize();

      const sessionId = session.getSessionId();
      console.log('[GreenRoom] Quick start - session ready, navigating to:', sessionId);

      navigate(`/room/${sessionId}`);
    } catch (err) {
      console.error('[GreenRoom] Quick start failed:', err);
      setError('Failed to start session. Please try again.');
      setIsLoading(false);
    }
  };

  // Enumerate devices on mount
  useEffect(() => {
    async function getDevices() {
      try {
        // Request permissions first
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const video = devices.filter(d => d.kind === 'videoinput');
        const audio = devices.filter(d => d.kind === 'audioinput');

        setVideoDevices(video);
        setAudioDevices(audio);

        if (video.length > 0) setSelectedVideoDevice(video[0].deviceId);
        if (audio.length > 0) setSelectedAudioDevice(audio[0].deviceId);
      } catch (err) {
        console.error('[GreenRoom] Failed to get devices:', err);
        setError('Please allow camera and microphone access to continue.');
      }
    }

    getDevices();
  }, []);

  // Update preview when device changes
  useEffect(() => {
    async function updatePreview() {
      if (previewStream) {
        previewStream.getTracks().forEach(track => track.stop());
      }

      if (isVideoOff && isMuted) {
        setPreviewStream(null);
        return;
      }

      try {
        const constraints = getMediaConstraints(selectedQuality, {
          video: isVideoOff ? undefined : selectedVideoDevice,
          audio: isMuted ? undefined : selectedAudioDevice
        });

        // Adjust constraints for preview
        if (isVideoOff) constraints.video = false;
        if (isMuted) constraints.audio = false;

        if (!constraints.video && !constraints.audio) {
          setPreviewStream(null);
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        setPreviewStream(stream);

        if (videoRef.current && stream.getVideoTracks().length > 0) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('[GreenRoom] Failed to get preview:', err);
        setError('Failed to access camera/microphone.');
      }
    }

    if (selectedVideoDevice || selectedAudioDevice) {
      updatePreview();
    }

    return () => {
      if (previewStream) {
        previewStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [selectedVideoDevice, selectedAudioDevice, selectedQuality, isVideoOff, isMuted]);

  // Toggle mute
  const toggleMute = () => {
    if (previewStream) {
      previewStream.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
    }
    setIsMuted(!isMuted);
  };

  // Toggle video
  const toggleVideo = () => {
    if (previewStream) {
      previewStream.getVideoTracks().forEach(track => {
        track.enabled = isVideoOff;
      });
    }
    setIsVideoOff(!isVideoOff);
  };

  // Start session (host)
  const startSession = async () => {
    console.log('[GreenRoom] startSession called');

    // Use a default title if not provided
    const title = sessionTitle.trim() || `Recording Session ${new Date().toLocaleString()}`;

    if (!token) {
      console.error('[GreenRoom] No auth token available');
      setError('Please log in to start a session.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const config: SessionConfig = {
        title: title,
        hostId: userId || 'host',
        hostName: userName || 'Host',
        quality: selectedQuality,
        enableChat,
        enableGreenRoom,
        enableRecording: true,
        maxParticipants
      };

      console.log('[GreenRoom] Creating session with config:', config);
      setSessionConfig(config);

      // Create session
      const session = createStudioSession(config, token, {
        onStateChange: (state) => {
          console.log('[GreenRoom] Session state changed:', state);
          useStudioStore.getState().setSessionState(state);
        },
        onError: (err) => {
          console.error('[Session] Error:', err);
          setError(err.message);
        }
      });

      console.log('[GreenRoom] Initializing session...');
      await session.initialize();

      const sessionId = session.getSessionId();
      console.log('[GreenRoom] Session initialized, navigating to:', sessionId);

      // Navigate to studio room
      navigate(`/room/${sessionId}`);
    } catch (err) {
      console.error('[GreenRoom] Failed to start session:', err);
      setError('Failed to start session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Join session (guest)
  const joinSession = async () => {
    console.log('[GreenRoom] joinSession called');
    console.log('[GreenRoom] Guest params:', { guestSessionId, guestMeetingId, guestToken: guestToken ? 'present' : 'missing' });

    if (!guestMeetingId) {
      setError('Invalid invite link - missing meeting ID.');
      return;
    }

    if (!guestToken) {
      setError('Invalid invite link - missing authentication token.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use guestMeetingId as the session ID for guests
      const roomId = guestSessionId || guestMeetingId;
      console.log('[GreenRoom] Navigating guest to room:', roomId);

      // For guests, we'll navigate to the room and handle joining there
      navigate(`/room/${roomId}?meeting=${guestMeetingId}&token=${guestToken}`);
    } catch (err) {
      console.error('[GreenRoom] Failed to join session:', err);
      setError('Failed to join session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="green-room">
      <div className="green-room-header">
        <img
          src="https://www.mypodops.com/assets/podops-logo.png"
          alt="MyPodOps"
          className="header-logo"
          style={{ height: '48px', marginBottom: '12px' }}
        />
        <p>{isGuest ? 'Join Recording Session' : (studioMode === 'record' ? 'Start New Recording' : 'Go Live')}</p>
      </div>

      {/* Mode Toggle - Record or Go Live (hosts only) */}
      {!isGuest && (
        <div className="mode-toggle">
          <button
            className={`mode-btn ${studioMode === 'record' ? 'active' : ''}`}
            onClick={() => setStudioMode('record')}
          >
            🎬 Record
          </button>
          <button
            className={`mode-btn ${studioMode === 'live' ? 'active' : ''}`}
            onClick={() => setStudioMode('live')}
          >
            📡 Go Live
          </button>
        </div>
      )}

      {/* Error Display - at top of page */}
      {error && (
        <div className="error-message error-banner">
          <span>⚠️</span>
          {error}
          <button className="error-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="green-room-content">
        {/* Go Live Mode - Simplified view */}
        {!isGuest && studioMode === 'live' && (
          <div className="golive-mode-section">
            <div className="golive-hero">
              <div className="golive-icon">📡</div>
              <h2>Stream to Multiple Platforms</h2>
              <p>Go live to YouTube, Twitch, Facebook, LinkedIn, and custom RTMP destinations simultaneously.</p>

              <div className="platform-icons">
                <span title="YouTube">▶️</span>
                <span title="Twitch">💜</span>
                <span title="Facebook">📘</span>
                <span title="LinkedIn">💼</span>
                <span title="Custom RTMP">🔗</span>
              </div>

              <button
                className="golive-start-btn"
                onClick={() => navigate('/live')}
              >
                🚀 Start Livestream Setup
              </button>

              <p className="golive-hint">
                Configure your stream settings, add platform destinations, and go live with one click.
              </p>
            </div>
          </div>
        )}

        {/* Recording Mode - Full device setup */}
        {(isGuest || studioMode === 'record') && (
        <div className="preview-section">
          <div className="video-preview">
            {isVideoOff ? (
              <div className="video-off-placeholder">
                <span className="avatar">{(userName || 'U')[0].toUpperCase()}</span>
                <p>Camera Off</p>
              </div>
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="preview-video"
              />
            )}

            {/* Audio Level Indicator */}
            <div className="audio-indicator">
              {!isMuted && <span className="audio-wave">🎤</span>}
            </div>
          </div>

          {/* Media Controls */}
          <div className="preview-controls">
            <button
              className={`control-btn ${isMuted ? 'off' : ''}`}
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : '🎤'}
            </button>
            <button
              className={`control-btn ${isVideoOff ? 'off' : ''}`}
              onClick={toggleVideo}
              title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            >
              {isVideoOff ? '📷' : '🎥'}
            </button>
          </div>

          {/* Device Selection */}
          <div className="device-selectors">
            <div className="device-select">
              <label>Camera</label>
              <select
                value={selectedVideoDevice}
                onChange={(e) => setSelectedVideoDevice(e.target.value)}
              >
                {videoDevices.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {truncateDeviceLabel(device.label) || `Camera ${videoDevices.indexOf(device) + 1}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="device-select">
              <label>Microphone</label>
              <select
                value={selectedAudioDevice}
                onChange={(e) => setSelectedAudioDevice(e.target.value)}
              >
                {audioDevices.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {truncateDeviceLabel(device.label) || `Microphone ${audioDevices.indexOf(device) + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Start Recording Button - inside preview-section for proper alignment */}
          <button
            className="start-btn"
            onClick={isGuest ? joinSession : startSession}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-small"></span>
                {isGuest ? 'Joining...' : 'Starting...'}
              </>
            ) : (
              <>
                {isGuest ? '🎬 Join Session' : '🎬 Start Recording'}
              </>
            )}
          </button>

          {!isGuest && (
            <p className="help-text">
              After starting, you can invite guests to join your session.
            </p>
          )}
        </div>
        )}

        {/* Session Settings (Host only, Record mode) */}
        {!isGuest && studioMode === 'record' && (
          <div className="settings-section">
            <h2>Session Settings</h2>

            <div className="setting-field">
              <label>Session Title</label>
              <input
                type="text"
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                placeholder="e.g., Podcast Episode 42"
              />
            </div>

            <div className="setting-field">
              <label>Recording Quality</label>
              <select
                value={selectedQuality}
                onChange={(e) => setSelectedQuality(e.target.value as keyof typeof QUALITY_PRESETS)}
              >
                <option value="4K">4K Ultra HD (3840x2160)</option>
                <option value="1080p">Full HD (1920x1080)</option>
                <option value="720p">HD (1280x720)</option>
                <option value="audio-only">Audio Only (48kHz)</option>
              </select>
            </div>

            <div className="setting-field">
              <label>Max Participants</label>
              <select
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(Number(e.target.value))}
              >
                <option value={2}>2 participants</option>
                <option value={5}>5 participants</option>
                <option value={10}>10 participants</option>
                <option value={17}>17 participants (max)</option>
              </select>
            </div>

            <div className="setting-toggles">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={enableChat}
                  onChange={(e) => setEnableChat(e.target.checked)}
                />
                <span>Enable Chat</span>
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={enableGreenRoom}
                  onChange={(e) => setEnableGreenRoom(e.target.checked)}
                />
                <span>Waiting Room for Guests</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="green-room-footer">
        <p>
          Powered by PodOps • 4K Video • 48kHz Audio • Local Recording
        </p>
      </div>
    </div>
  );
}
